// CORE AI Tutor for teachers — the "Perla" owl from the C.O.R.E. brand (same as CORE for Business),
// a floating button with eye-tracking + blink, a greeting bubble and a closed-domain chat panel.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiSend, FiX, FiVolume2, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND, GOLD_GRADIENT } from '../common/ui';

const STORAGE = 'core-tutor-chat-v1';
const SUGGESTIONS = [
  'How do I run the password lab of Lesson 1 in class?',
  'Explain ransomware the way Lesson 15 does, for a 12-year-old.',
  'Give me the answer key for the Lesson 7 quiz.',
  'Ideas for a 10-minute warm-up before Lesson 4 (phishing).',
];

/* ---------- the owl (SVG shared with the brand site) ---------- */
export function PerlaOwl({ size = 68, track = true, className = '', style }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!track) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const pupils = el.querySelectorAll('.owl-pupil, .owl-iris');
    const MAX = 3.2;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const dx = e.clientX - cx, dy = e.clientY - cy;
      const d = Math.hypot(dx, dy) || 1;
      const k = Math.min(1, d / 260);
      const tx = (dx / d) * MAX * k, ty = (dy / d) * MAX * k;
      pupils.forEach((p) => { p.style.transform = `translate(${tx}px, ${ty}px)`; });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [track]);
  return (
    <svg ref={ref} className={`perla-owl ${className}`} viewBox="0 0 120 120" width={size} height={size} style={style} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <radialGradient id="pOwlPearl" cx="45%" cy="35%" r="70%"><stop offset="0" stopColor="#ffffff" /><stop offset=".7" stopColor="#f1ece5" /><stop offset="1" stopColor="#d9d1c6" /></radialGradient>
        <radialGradient id="pOwlNavy" cx="45%" cy="35%" r="75%"><stop offset="0" stopColor="#2f4270" /><stop offset=".65" stopColor="#16274a" /><stop offset="1" stopColor="#0b1730" /></radialGradient>
        <linearGradient id="pOwlGold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f7d58f" /><stop offset="1" stopColor="#b8843d" /></linearGradient>
        <radialGradient id="pOwlIris" cx="40%" cy="35%" r="70%"><stop offset="0" stopColor="#ffe3a6" /><stop offset=".6" stopColor="#d9a55a" /><stop offset="1" stopColor="#8d632c" /></radialGradient>
      </defs>
      <ellipse cx="60" cy="110" rx="26" ry="4" fill="#12213c" opacity=".12" />
      <ellipse cx="27" cy="68" rx="13" ry="25" transform="rotate(-10 27 68)" fill="url(#pOwlNavy)" />
      <ellipse cx="93" cy="68" rx="13" ry="25" transform="rotate(10 93 68)" fill="url(#pOwlNavy)" />
      <path d="M30 30 L36 12 L48 28 Z" fill="url(#pOwlPearl)" stroke="#12213c" strokeWidth="1.2" strokeOpacity=".5" />
      <path d="M90 30 L84 12 L72 28 Z" fill="url(#pOwlPearl)" stroke="#12213c" strokeWidth="1.2" strokeOpacity=".5" />
      <ellipse cx="60" cy="68" rx="38" ry="40" fill="url(#pOwlPearl)" stroke="#12213c" strokeWidth="1.6" strokeOpacity=".55" />
      <path d="M28 54 C28 40 40 34 48 38 C54 40 58 44 60 48 C62 44 66 40 72 38 C80 34 92 40 92 54 C92 66 82 72 74 70 C66 68 62 62 60 58 C58 62 54 68 46 70 C38 72 28 66 28 54 Z" fill="url(#pOwlNavy)" />
      <path d="M34 42 C40 34 50 34 57 42" fill="none" stroke="#12213c" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M86 42 C80 34 70 34 63 42" fill="none" stroke="#12213c" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="46" cy="54" r="13" fill="#fbf3e8" stroke="rgba(205,155,80,.9)" strokeWidth="2" />
      <circle cx="74" cy="54" r="13" fill="#fbf3e8" stroke="rgba(205,155,80,.9)" strokeWidth="2" />
      <circle className="owl-iris" cx="48" cy="55" r="8.5" fill="url(#pOwlIris)" style={{ transition: 'transform .15s ease-out' }} />
      <circle className="owl-iris" cx="76" cy="55" r="8.5" fill="url(#pOwlIris)" style={{ transition: 'transform .15s ease-out' }} />
      <circle className="owl-pupil" cx="48" cy="55" r="4.2" fill="#0b1730" style={{ transition: 'transform .15s ease-out' }} />
      <circle className="owl-pupil" cx="76" cy="55" r="4.2" fill="#0b1730" style={{ transition: 'transform .15s ease-out' }} />
      <circle cx="51" cy="51" r="2.2" fill="#fff" opacity=".9" /><circle cx="79" cy="51" r="2.2" fill="#fff" opacity=".9" />
      <ellipse className="owl-eyelid" cx="46" cy="54" rx="13.5" ry="13.5" fill="#16274a" />
      <ellipse className="owl-eyelid" cx="74" cy="54" rx="13.5" ry="13.5" fill="#16274a" />
      <path d="M60 62 L54.5 70 L65.5 70 Z" fill="url(#pOwlGold)" />
      <path d="M50 84 q5 5 10 0 q5 5 10 0 M46 92 q5 5 10 0 q5 5 10 0 q5 5 10 0" fill="none" stroke="#12213c" strokeWidth="1.3" opacity=".35" strokeLinecap="round" />
      <ellipse cx="50" cy="107" rx="7" ry="3" fill="url(#pOwlGold)" /><ellipse cx="70" cy="107" rx="7" ry="3" fill="url(#pOwlGold)" />
    </svg>
  );
}

const css = `
.perla-owl .owl-eyelid{transform-origin:center;transform:scaleY(0);animation:perla-blink 4.6s ease-in-out infinite}
@keyframes perla-blink{0%,92%,100%{transform:scaleY(0)}96%{transform:scaleY(1)}}
.core-tutor-fab{position:fixed;right:26px;bottom:26px;z-index:9000;width:76px;height:76px;border-radius:50%;border:none;cursor:pointer;padding:0;display:grid;place-items:center;
  background:radial-gradient(circle at 40% 30%, #1b2d52, #07142d 70%);box-shadow:0 14px 40px rgba(7,20,45,.45), 0 0 0 1px rgba(205,155,80,.45) inset;transition:transform .2s}
.core-tutor-fab:hover{transform:translateY(-3px) scale(1.04)}
.core-tutor-fab .ring{position:absolute;inset:-5px;border-radius:50%;border:2px solid rgba(205,155,80,.45);animation:perla-ring 2.4s ease-out infinite;pointer-events:none}
@keyframes perla-ring{0%{transform:scale(1);opacity:.7}100%{transform:scale(1.45);opacity:0}}
.core-tutor-bubble{position:fixed;right:116px;bottom:44px;z-index:9000;max-width:270px;background:#fff;color:#0b1224;border-radius:16px 16px 4px 16px;padding:12px 14px;font-size:.9rem;line-height:1.4;
  box-shadow:0 14px 40px rgba(7,20,45,.25);border:1px solid rgba(205,155,80,.35);font-family:${BRAND.font}}
.core-tutor-bubble b{color:#8d632c}
.core-tutor-panel{position:fixed;right:26px;bottom:26px;z-index:9001;width:min(420px, calc(100vw - 32px));height:min(640px, calc(100vh - 110px));display:flex;flex-direction:column;
  background:linear-gradient(180deg, rgba(11,26,58,.97), rgba(7,20,45,.98));border:1px solid rgba(205,155,80,.35);border-radius:24px;box-shadow:0 30px 90px rgba(0,0,0,.55);overflow:hidden;font-family:${BRAND.font};color:${BRAND.ivory}}
.core-tutor-msgs{flex:1;overflow-y:auto;padding:16px 16px 8px;display:flex;flex-direction:column;gap:12px;scrollbar-width:thin}
.core-tutor-msg{max-width:88%;padding:11px 14px;border-radius:16px;font-size:.92rem;line-height:1.5;white-space:pre-wrap;word-break:break-word}
.core-tutor-msg.user{align-self:flex-end;background:${GOLD_GRADIENT};color:#07142d;border-bottom-right-radius:5px;font-weight:600}
.core-tutor-msg.bot{align-self:flex-start;background:rgba(249,240,231,.08);border:1px solid rgba(249,240,231,.1);border-bottom-left-radius:5px}
.core-tutor-msg.bot b{color:${BRAND.goldLight}}
.core-tutor-msg.bot ul{margin:6px 0 0 18px;padding:0}
.core-tutor-msg.bot li{margin:2px 0}
.core-tutor-src{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.core-tutor-src a{font-size:.72rem;font-weight:800;letter-spacing:.04em;color:${BRAND.goldLight};border:1px solid rgba(205,155,80,.4);border-radius:999px;padding:3px 9px;text-decoration:none;background:rgba(205,155,80,.1)}
.core-tutor-chip{border:1px solid rgba(205,155,80,.4);background:rgba(205,155,80,.08);color:${BRAND.ivory};border-radius:12px;padding:9px 12px;text-align:left;font-size:.84rem;cursor:pointer;font-family:inherit;line-height:1.35}
.core-tutor-chip:hover{background:rgba(205,155,80,.18)}
.core-tutor-dots span{display:inline-block;width:6px;height:6px;border-radius:50%;background:${BRAND.goldLight};margin-right:4px;animation:perla-dot 1.2s infinite}
.core-tutor-dots span:nth-child(2){animation-delay:.2s}.core-tutor-dots span:nth-child(3){animation-delay:.4s}
@keyframes perla-dot{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-3px)}}
@media (max-width:640px){.core-tutor-fab{right:14px;bottom:14px;width:64px;height:64px}.core-tutor-bubble{right:90px;bottom:28px}.core-tutor-panel{right:8px;bottom:8px;left:8px;width:auto}}
`;

// very small markdown: **bold**, "- " bullets, "### " headings, blank-line paragraphs;
// horizontal rules (---) are dropped.
function renderMd(text) {
  const blocks = String(text)
    .replace(/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/gm, '')
    .split(/\n{2,}/)
    .filter((b) => b.trim());
  return blocks.map((b, i) => {
    const lines = b.split('\n').filter((l) => l.trim());
    const isList = lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l));
    const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith('**') && p.endsWith('**') ? <b key={j}>{p.slice(2, -2)}</b> : p));
    if (isList) return <ul key={i}>{lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''))}</li>)}</ul>;
    if (lines.length === 1 && /^#{1,4}\s+/.test(lines[0])) {
      return <p key={i} style={{ margin: i ? '10px 0 0' : 0, fontWeight: 800 }}>{inline(lines[0].replace(/^#{1,4}\s+/, ''))}</p>;
    }
    return <p key={i} style={{ margin: i ? '8px 0 0' : 0 }}>{lines.map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(l.replace(/^#{1,4}\s+/, ''))}</React.Fragment>)}</p>;
  });
}

export default function TutorOwl() {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(false);
  const [msgs, setMsgs] = useState(() => { try { return JSON.parse(sessionStorage.getItem(STORAGE) || '[]'); } catch { return []; } });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const onAdminPage = /^\/(admin|platform)/.test(location.pathname);
  const inDeck = /^\/admin\/lezioni\//.test(location.pathname); // full-screen lesson player: keep the stage clean

  useEffect(() => {
    if (!isAdmin || !onAdminPage) return;
    api.get('/tutor/status').then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false }));
  }, [isAdmin, onAdminPage]);

  useEffect(() => {
    if (!isAdmin || !onAdminPage || open) return undefined;
    if (sessionStorage.getItem('core-tutor-greeted')) return undefined;
    const t1 = setTimeout(() => setBubble(true), 1800);
    const t2 = setTimeout(() => { setBubble(false); sessionStorage.setItem('core-tutor-greeted', '1'); }, 9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isAdmin, onAdminPage, open]);

  useEffect(() => { sessionStorage.setItem(STORAGE, JSON.stringify(msgs.slice(-30))); }, [msgs]);
  useEffect(() => { if (open) { setTimeout(() => inputRef.current?.focus(), 150); } }, [open]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, busy, open]);

  const send = useCallback(async (text) => {
    const q = String(text || input).trim();
    if (!q || busy) return;
    setInput('');
    const history = msgs.slice(-8).map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post('/tutor/chat', { message: q, history });
      setMsgs((m) => [...m, { role: 'assistant', content: data.reply, sources: data.sources || [] }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: 'assistant', content: e.response?.data?.error || 'I could not answer right now. Please try again.', error: true }]);
    } finally { setBusy(false); }
  }, [input, busy, msgs]);

  const speak = (text) => {
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(String(text).replace(/\*\*/g, ''));
      u.lang = /[àèéìòù]/i.test(text) ? 'it-IT' : 'en-US';
      u.rate = 1;
      window.speechSynthesis.speak(u);
    } catch { /* no TTS */ }
  };

  if (!isAdmin || !onAdminPage || inDeck) return null;

  const firstName = (user?.name || '').split(' ')[0];

  return (
    <>
      <style>{css}</style>
      <AnimatePresence>
        {bubble && !open && (
          <motion.div key="bubble" className="core-tutor-bubble" initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6 }} onClick={() => { setBubble(false); setOpen(true); }} role="button">
            Hi{firstName ? ` ${firstName}` : ''}! I'm the <b>CORE Tutor</b>. Ask me anything about the 18 lessons, the missions or how to run them in class.
          </motion.div>
        )}
      </AnimatePresence>

      {!open && (
        <motion.button type="button" className="core-tutor-fab" onClick={() => { setBubble(false); setOpen(true); }} aria-label="Open the CORE AI tutor" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.4 }}>
          <span className="ring" />
          <PerlaOwl size={62} />
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.section key="panel" className="core-tutor-panel" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.97 }} transition={{ type: 'spring', stiffness: 160, damping: 20 }} aria-label="CORE AI tutor">
            <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: `1px solid ${BRAND.line}`, background: 'rgba(7,20,45,0.6)' }}>
              <span style={{ width: 46, height: 46, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'radial-gradient(circle at 40% 30%, #1b2d52, #07142d 70%)', boxShadow: '0 0 0 1px rgba(205,155,80,.45) inset' }}><PerlaOwl size={40} track={false} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: '1rem', lineHeight: 1.1 }}>CORE Tutor</div>
                <div style={{ fontSize: '0.74rem', color: BRAND.muted, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {status?.enabled ? `Teacher assistant · ${status.lessons || 18} lessons` : 'Teacher assistant'}
                </div>
              </div>
              {msgs.length > 0 && <button type="button" onClick={() => setMsgs([])} title="New conversation" style={{ background: 'transparent', border: 'none', color: BRAND.muted, cursor: 'pointer', padding: 6 }}><FiRefreshCw size={16} /></button>}
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'rgba(249,240,231,0.08)', border: `1px solid rgba(249,240,231,0.14)`, color: BRAND.ivory, cursor: 'pointer', width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center' }}><FiX size={17} /></button>
            </header>

            <div className="core-tutor-msgs" ref={listRef}>
              {msgs.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div className="core-tutor-msg bot" style={{ maxWidth: '100%' }}>
                    Hi{firstName ? ` ${firstName}` : ''}! I only talk about <b>CORE for Schools</b> — the 18 lessons, their missions, quizzes and how to bring them to your class. What would you like to know?
                  </div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND.gold, marginTop: 4 }}>Try asking</div>
                  {SUGGESTIONS.map((s) => <button key={s} type="button" className="core-tutor-chip" onClick={() => send(s)}>{s}</button>)}
                  {status && !status.enabled && (
                    <div style={{ fontSize: '0.8rem', color: BRAND.warning, border: `1px solid rgba(226,184,120,0.4)`, borderRadius: 12, padding: '8px 10px', marginTop: 4 }}>
                      The tutor is not configured yet on this server (missing AI key). Ask CORE to enable it.
                    </div>
                  )}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`core-tutor-msg ${m.role === 'user' ? 'user' : 'bot'}`} style={m.error ? { borderColor: 'rgba(224,122,122,0.5)', color: '#f3b1b1' } : undefined}>
                  {m.role === 'user' ? m.content : renderMd(m.content)}
                  {m.role === 'assistant' && !m.error && (
                    <div className="core-tutor-src">
                      {(m.sources || []).map((s) => <Link key={s.lessonId} to={`/admin/lezioni/${s.lessonId}`} onClick={() => setOpen(false)}>Lesson {s.lessonOrder}</Link>)}
                      <button type="button" onClick={() => speak(m.content)} title="Read aloud" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: BRAND.muted, cursor: 'pointer', padding: 2, display: 'inline-flex' }}><FiVolume2 size={15} /></button>
                    </div>
                  )}
                </div>
              ))}
              {busy && <div className="core-tutor-msg bot core-tutor-dots"><span /><span /><span /></div>}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{ display: 'flex', gap: 8, padding: '10px 12px 12px', borderTop: `1px solid ${BRAND.line}`, background: 'rgba(7,20,45,0.6)' }}>
              <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about a lesson, a mission, a quiz…" maxLength={2000} disabled={busy || (status && !status.enabled)}
                style={{ flex: 1, padding: '12px 14px', borderRadius: 14, border: `1px solid rgba(249,240,231,0.16)`, background: 'rgba(7,20,45,0.7)', color: BRAND.ivory, fontFamily: BRAND.font, fontSize: '0.93rem', outline: 'none' }} />
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={busy || !input.trim()} aria-label="Send"
                style={{ width: 46, height: 46, borderRadius: 14, border: 'none', background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center', cursor: busy || !input.trim() ? 'not-allowed' : 'pointer', opacity: busy || !input.trim() ? 0.6 : 1 }}>
                <FiSend size={18} />
              </motion.button>
            </form>
            <div style={{ fontSize: '0.68rem', color: BRAND.faint, textAlign: 'center', padding: '0 12px 8px', background: 'rgba(7,20,45,0.6)' }}>Answers come only from the CORE for Schools course material.</div>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}
